import type { CoreLoopTabId } from "@/features/hoverIdle/hov/hovView";
import type {
  HoverIdleSnapshot,
  WallNotificationMode,
} from "@/features/hoverIdle/types";
import {
  WALL_DEFINITIONS,
  type WallId,
} from "@/features/hoverIdle/wall/wallDefinitions";

export interface DispatchWallClearNotificationOptions {
  readonly beforeSnapshot: HoverIdleSnapshot;
  readonly afterSnapshot: HoverIdleSnapshot;
  readonly mode: WallNotificationMode;
  readonly activeHovTabId: CoreLoopTabId;
  readonly showToast: (wallId: WallId) => void;
  readonly markSeen: (wallId: WallId) => boolean;
}

export interface DispatchWallClearNotificationResult {
  readonly markedSeen: boolean;
}

export function dispatchWallClearNotifications(
  options: DispatchWallClearNotificationOptions,
): DispatchWallClearNotificationResult {
  const clearedWallIds = resolveClearedWallIds(
    options.beforeSnapshot,
    options.afterSnapshot,
  );
  if (clearedWallIds.length === 0) {
    return { markedSeen: false };
  }

  if (options.mode === "full") {
    for (const wallId of clearedWallIds) {
      options.showToast(wallId);
    }
  }

  let markedSeen = false;
  if (options.activeHovTabId === "hov-wall") {
    for (const wallId of clearedWallIds) {
      markedSeen = options.markSeen(wallId) || markedSeen;
    }
  }

  return { markedSeen };
}

export function resolveClearedWallIds(
  beforeSnapshot: HoverIdleSnapshot,
  afterSnapshot: HoverIdleSnapshot,
): readonly WallId[] {
  const clearedWallIds: WallId[] = [];
  const seen = new Set<WallId>();

  // Active wall transition: the wall that was active before and is no longer
  // active in the after-snapshot is the clear event for this tick.
  const beforeActiveWallId = beforeSnapshot.wall.activeWallId;
  if (
    beforeActiveWallId !== null &&
    afterSnapshot.wall.activeWallId !== beforeActiveWallId &&
    isKnownWallId(beforeActiveWallId)
  ) {
    clearedWallIds.push(beforeActiveWallId);
    seen.add(beforeActiveWallId);
  }

  // Fallback: any wall id that newly appears in unseenClearedWallIds and was
  // not already covered by the active-wall transition above.
  const previouslyUnseen = new Set(beforeSnapshot.wall.unseenClearedWallIds);
  for (const wallId of afterSnapshot.wall.unseenClearedWallIds) {
    if (previouslyUnseen.has(wallId)) continue;
    if (seen.has(wallId)) continue;
    clearedWallIds.push(wallId);
    seen.add(wallId);
  }

  return clearedWallIds;
}

const KNOWN_WALL_IDS = new Set<WallId>(WALL_DEFINITIONS.map((wall) => wall.id));

function isKnownWallId(wallId: WallId): boolean {
  return KNOWN_WALL_IDS.has(wallId);
}
